import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { nuclearn_api_url } from "src/App";

// Compute the base at call time, not module load (bootstrap TDZ hazard).
const base = () => `${nuclearn_api_url}/v1/fifty-fifty-nine`;

export interface FfnReferenceDocument {
  id: number;
  role: string;
  dataset_id: number;
  dataset_name: string | null;
  description: string | null;
}

export interface FfnPlant {
  id: number;
  code: string;
  name: string;
  description: string | null;
  reference_documents: FfnReferenceDocument[];
}

export interface FfnChallengeGrade {
  item_id: string;
  verdict: "pass" | "weak" | "fail" | "not_applicable";
  evidence: string;
  required_revision?: string | null;
}

export interface FfnVerifiedClaim {
  claim: string;
  document_location: string;
  claim_type: string;
  cited_source?: string | null;
  verdict: string;
  evidence: string;
  research_direction?: string | null;
}

export interface FfnReportVerification {
  id: number;
  status: string;
  tier?: string | null;
  counts?: Record<string, number> | null;
  claims?: FfnVerifiedClaim[] | null;
  summary?: string | null;
  dialogue_id?: number | null;
  create_date?: string | null;
}

/** One record as returned by GET/POST /v1/fifty-fifty-nine/records. */
export interface FfnRecordRow {
  id: number;
  plant_id: number;
  plant_name?: string | null;
  title: string;
  status: string;
  activity_description: string;
  applicability?: Record<string, any> | null;
  screening?: Record<string, any> | null;
  evaluation?: Record<string, any> | null;
  evaluation_criteria_missing: number[];
  challenge?: {
    grades?: FfnChallengeGrade[];
    summary?: string;
    narrative?: string | null;
    passed?: boolean;
    failed_items?: string[];
    weak_items?: string[];
  } | null;
  conclusion?: string | null;
  research_log: Array<{
    source: string;
    reference?: string | null;
    note: string;
  }>;
  report_artifact_id?: number | null;
  report_verification?: FfnReportVerification | null;
  dialogue_id?: number | null;
  updated_date?: string | null;
}

export const getFfnRecords = async (params: {
  plant_id?: number;
  status?: string;
  limit?: number;
}): Promise<FfnRecordRow[]> => {
  const response = await axios.get(`${base()}/records`, { params });
  return response.data;
};

export const useFfnRecords = (filters: {
  plant_id?: number;
  status?: string;
}) => {
  // Same reasoning as the PG runs list: refetch on focus, no polling.
  return useQuery(
    ["ffnRecords", filters.plant_id ?? null, filters.status ?? null],
    () => getFfnRecords({ ...filters, limit: 200 }),
    { refetchOnWindowFocus: true, staleTime: 30 * 1000 },
  );
};

export const verifyFfnRecordReport = async (
  recordId: number,
): Promise<FfnRecordRow> => {
  const response = await axios.post(`${base()}/records/${recordId}/verify`);
  return response.data;
};

const getPlants = async (): Promise<FfnPlant[]> => {
  const response = await axios.get(`${base()}/plants`);
  return response.data;
};

export const useFfnPlants = () => {
  return useQuery(["ffnPlants"], getPlants, {
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateFfnPlant = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (body: { name: string; description?: string; code?: string }) =>
      axios.post(`${base()}/plants`, body).then((r) => r.data as FfnPlant),
    {
      onSuccess: () => queryClient.invalidateQueries(["ffnPlants"]),
    },
  );
};

export const useAddFfnReferenceDocument = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      plantId,
      role,
      datasetId,
      description,
    }: {
      plantId: number;
      role: string;
      datasetId: number;
      description?: string;
    }) =>
      axios
        .post(`${base()}/plants/${plantId}/reference-documents`, {
          role,
          dataset_id: datasetId,
          description,
        })
        .then((r) => r.data),
    {
      onSuccess: () => queryClient.invalidateQueries(["ffnPlants"]),
    },
  );
};

export const useRemoveFfnReferenceDocument = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (referenceDocumentId: number) =>
      axios.delete(`${base()}/reference-documents/${referenceDocumentId}`),
    {
      onSuccess: () => queryClient.invalidateQueries(["ffnPlants"]),
    },
  );
};

/**
 * Create the 50.59 record at kickoff, linked to the dialogue that will work
 * it — the record-side mirror of PG's create-run. Linking before the first
 * turn is what lets the backend inject the record's state from turn one.
 */
export const createFfnRecord = async (body: {
  plant_id: number;
  title: string;
  activity_description: string;
  dialogue_id: number;
}): Promise<FfnRecordRow> => {
  const response = await axios.post(`${base()}/records`, body);
  return response.data;
};
